const ALLOWED_TARGET_MODES = new Set([
  'existing_lead',
  'existing_case',
  'create_lead',
  'create_case',
]);

const ALLOWED_FIELD_TYPES = new Set([
  'text',
  'textarea',
  'email',
  'tel',
  'number',
  'currency',
  'percent',
  'date',
  'time',
  'select',
  'radio',
  'checkbox',
  'checkboxes',
  'boolean',
  'upload',
]);

const RESERVED_FIELD_IDS = new Set(['__proto__', 'prototype', 'constructor']);

const TYPE_ALIASES = Object.freeze({
  string: 'text',
  long_text: 'textarea',
  phone: 'tel',
  integer: 'number',
  decimal: 'number',
  money: 'currency',
  dropdown: 'select',
  multiselect: 'checkboxes',
  multi_select: 'checkboxes',
  yes_no: 'boolean',
  file: 'upload',
  files: 'upload',
  document: 'upload',
});

const NON_REPEATABLE_TYPES = new Set(['boolean', 'checkbox', 'checkboxes', 'radio']);

const CONTENT_TYPES = new Set(['subtitle', 'instruction', 'step_title', 'step_instruction']);
const STEP_CONTENT_TYPES = new Set(['step_title', 'step_instruction']);

const normalizeRepeatGroup = (value, contentType) => {
  const compiledUid = String(value?.uid || '').trim();
  if (contentType !== 'subtitle' || !value || (value.enabled !== true && !/^group\.[a-f0-9]{24}$/.test(compiledUid))) return null;
  return {
    enabled: true,
    entry_label: cleanText(value.entry_label, 80) || 'Entry',
    add_label: cleanText(value.add_label, 120) || 'Add another entry',
  };
};

export const PUBLIC_FORM_CONTENT_TYPE_OPTIONS = Object.freeze([
  Object.freeze({ value: 'subtitle', label: 'Subtitle' }),
  Object.freeze({ value: 'instruction', label: 'Instructions' }),
]);

export const CUSTOM_QUESTION_TYPE_OPTIONS = Object.freeze([
  Object.freeze({ value: 'text', label: 'Short text' }),
  Object.freeze({ value: 'number', label: 'Number' }),
  Object.freeze({ value: 'textarea', label: 'Long text' }),
  Object.freeze({ value: 'select', label: 'Select' }),
  Object.freeze({ value: 'checkboxes', label: 'Multiple choice' }),
  Object.freeze({ value: 'checkbox', label: 'Checkbox' }),
]);

const CUSTOM_QUESTION_TYPES = new Set(CUSTOM_QUESTION_TYPE_OPTIONS.map((option) => option.value));

const CATALOG_GROUP_TITLES = Object.freeze({
  built_in: 'Built-in fields',
  builtin: 'Built-in fields',
  custom_field: 'Custom fields',
  custom_fields: 'Custom fields',
  custom_question: 'Custom questions',
});

const FIELD_TYPE_TITLES = Object.freeze({
  text: 'Short text',
  textarea: 'Long text',
  email: 'Email',
  tel: 'Phone',
  number: 'Number',
  currency: 'Currency',
  percent: 'Percentage',
  date: 'Date',
  time: 'Time',
  select: 'Select',
  radio: 'Single choice',
  checkbox: 'Checkbox',
  checkboxes: 'Multiple choice',
  boolean: 'Yes or no',
  upload: 'Document upload',
  subtitle: 'Subtitle',
  instruction: 'Instructions',
  step_title: 'Step title',
  step_instruction: 'Step instructions',
});

const cleanText = (value, maxLength = 240) => Array.from(String(value ?? ''))
  .map((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127 ? ' ' : character;
  })
  .join('')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, maxLength);

const cleanFieldId = (value) => {
  const id = String(value ?? '').trim();
  if (!/^[A-Za-z0-9_.:-]{1,128}$/.test(id) || RESERVED_FIELD_IDS.has(id)) return '';
  return id;
};

const normalizeFieldType = (value) => {
  const raw = String(value || 'text').trim().toLowerCase();
  const normalized = TYPE_ALIASES[raw] || raw;
  return ALLOWED_FIELD_TYPES.has(normalized) ? normalized : 'text';
};

const normalizeOptions = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value.slice(0, 100).reduce((options, option) => {
    const rawValue = option && typeof option === 'object'
      ? option.value ?? option.id ?? option.label ?? option.name
      : option;
    const rawLabel = option && typeof option === 'object'
      ? option.label ?? option.name ?? option.value ?? option.id
      : option;
    const optionValue = cleanText(rawValue, 160);
    const label = cleanText(rawLabel, 160);
    if (!optionValue || !label || seen.has(optionValue)) return options;
    seen.add(optionValue);
    options.push({ value: optionValue, label });
    return options;
  }, []);
};

export const parsePublicFormOptionLines = (value) => normalizeOptions(
  String(value ?? '')
    .split(/\r?\n/)
    .map((option) => option.trim())
    .filter(Boolean),
);

export const publicFormCatalogGroupTitle = (value) => {
  const raw = cleanText(value, 80);
  if (!raw) return 'Other fields';
  const key = raw.toLowerCase().replace(/[\s-]+/g, '_');
  return CATALOG_GROUP_TITLES[key] || raw.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
};

export const publicFormFieldTypeTitle = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return FIELD_TYPE_TITLES[raw] || FIELD_TYPE_TITLES[normalizeFieldType(raw)] || 'Field';
};

export const isPublicFormContentItem = (item) => (
  item?.kind === 'content'
  || CONTENT_TYPES.has(String(item?.content_type || '').toLowerCase())
  || (item?.source === 'presentation' && CONTENT_TYPES.has(String(item?.type || '').toLowerCase()))
);

export const isPublicFormStepContentItem = (item) => (
  isPublicFormContentItem(item)
  && STEP_CONTENT_TYPES.has(String(item?.content_type || item?.type || '').toLowerCase())
);

export const isStandalonePublicFormField = (field) => {
  if (isPublicFormContentItem(field)) return true;
  const fieldKey = String(field?.field_key || '');
  return field?.source === 'custom_question'
    || field?.source === 'document_upload'
    || fieldKey.startsWith('question.')
    || fieldKey.startsWith('upload.')
    || /^document_upload_[1-9][0-9]*$/.test(fieldKey)
    || field?.type === 'upload';
};

export const sanitizePublicFormCatalog = (catalog) => {
  if (!Array.isArray(catalog)) return [];
  const seen = new Set();
  return catalog.slice(0, 500).reduce((fields, rawField) => {
    if (!rawField || typeof rawField !== 'object') return fields;
    const id = cleanFieldId(rawField.field_key ?? rawField.uid ?? rawField.id ?? rawField.key ?? rawField.field_id);
    if (!id || seen.has(id)) return fields;
    const label = cleanText(rawField.label ?? rawField.name, 160);
    if (!label) return fields;
    seen.add(id);
    fields.push({
      id,
      field_key: id,
      field_id: Number(rawField.field_id) > 0 ? Number(rawField.field_id) : null,
      label,
      type: normalizeFieldType(rawField.type ?? rawField.input_type ?? rawField.field_type),
      group: cleanText(rawField.group ?? rawField.section ?? rawField.source, 80) || 'Other',
      help_text: cleanText(rawField.help_text ?? rawField.description, 500),
      options: normalizeOptions(rawField.options),
      writable: rawField.writable !== false && rawField.read_only !== true,
      required_by_system: rawField.required_by_system === true,
      source: cleanText(rawField.source ?? rawField.table, 80),
      applicable_modes: Array.isArray(rawField.applicable_modes)
        ? rawField.applicable_modes.filter((mode) => ALLOWED_TARGET_MODES.has(mode))
        : [],
    });
    return fields;
  }, []).filter((field) => field.writable);
};

export const sanitizePublicFormCaseTypes = (caseTypes) => {
  if (!Array.isArray(caseTypes)) return [];
  const seen = new Set();
  return caseTypes.slice(0, 500).reduce((items, rawCaseType) => {
    const id = Number(rawCaseType?.id);
    const name = cleanText(rawCaseType?.name, 160);
    if (!Number.isInteger(id) || id <= 0 || !name || seen.has(id)) return items;
    seen.add(id);
    items.push({
      id,
      name,
      description: cleanText(rawCaseType?.description, 500),
    });
    return items;
  }, []);
};

let fieldSequence = 0;

export const createSelectedPublicFormField = (catalogField, existingFields = []) => {
  const sourceId = cleanFieldId(catalogField?.field_key ?? catalogField?.id);
  if (!sourceId) return null;
  const duplicateCount = existingFields.filter((field) => field.field_key === sourceId).length;
  fieldSequence += 1;
  const instanceId = `${sourceId}:${Date.now().toString(36)}:${fieldSequence.toString(36)}`;
  return {
    instance_id: instanceId,
    field_key: sourceId,
    field_id: Number(catalogField?.field_id) > 0 ? Number(catalogField.field_id) : null,
    label: cleanText(catalogField.label, 160) || 'Untitled field',
    type: normalizeFieldType(catalogField.type),
    help_text: cleanText(catalogField.help_text, 500),
    placeholder: '',
    options: normalizeOptions(catalogField.options),
    required: Boolean(catalogField.required_by_system),
    repeatable: false,
    step: 1,
    condition: null,
    duplicate_index: duplicateCount,
    source: cleanText(catalogField?.source, 80),
  };
};

const createOpaqueFieldKey = (namespace, existingFields) => {
  const occupied = new Set((Array.isArray(existingFields) ? existingFields : []).map((field) => field?.field_key));
  let candidate = '';
  do {
    fieldSequence += 1;
    candidate = `${namespace}${Date.now().toString(36)}${fieldSequence.toString(36)}`.toLowerCase();
  } while (occupied.has(candidate));
  return candidate;
};

export const createCustomPublicFormField = (type = 'text', existingFields = []) => {
  const normalizedType = CUSTOM_QUESTION_TYPES.has(type) ? type : 'text';
  const fieldKey = createOpaqueFieldKey('question.', existingFields);
  return {
    instance_id: `${fieldKey}:instance`,
    field_key: fieldKey,
    field_id: null,
    source: 'custom_question',
    label: 'Custom question',
    type: normalizedType,
    help_text: '',
    placeholder: '',
    options: [],
    options_text: '',
    required: false,
    repeatable: false,
    step: 1,
    condition: null,
    duplicate_index: 0,
  };
};

export const createPublicFormContentItem = (type = 'subtitle', existingItems = []) => {
  const contentType = CONTENT_TYPES.has(type) ? type : 'subtitle';
  const itemKey = createOpaqueFieldKey('content.', existingItems);
  return {
    instance_id: `${itemKey}:instance`,
    kind: 'content',
    content_type: contentType,
    source: 'presentation',
    type: contentType,
    text: contentType === 'subtitle'
      ? 'Section title'
      : contentType === 'instruction'
        ? 'Add instructions for this section.'
        : '',
    step: 1,
    repeat_group: null,
  };
};

export const createUploadPublicFormField = (existingFields = []) => {
  const uploadCount = existingFields.filter((field) => field.type === 'upload').length;
  const fieldKey = createOpaqueFieldKey('upload.', existingFields);
  return {
    instance_id: `${fieldKey}:instance`,
    field_key: fieldKey,
    field_id: null,
    source: 'document_upload',
    label: uploadCount === 0 ? 'Documents' : `Documents ${uploadCount + 1}`,
    type: 'upload',
    help_text: 'Choose documents or photos from this device.',
    placeholder: '',
    options: [],
    required: false,
    repeatable: true,
    step: 1,
    condition: null,
    duplicate_index: uploadCount,
  };
};

export const movePublicFormField = (fields, fromIndex, toIndex) => {
  const list = Array.isArray(fields) ? [...fields] : [];
  if (
    !Number.isInteger(fromIndex)
    || !Number.isInteger(toIndex)
    || fromIndex < 0
    || toIndex < 0
    || fromIndex >= list.length
    || toIndex >= list.length
    || fromIndex === toIndex
  ) {
    return list;
  }
  const [field] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, field);
  return list;
};

const normalizeCreationId = (value) => {
  if (!/^[1-9]\d*$/.test(String(value ?? ''))) return '';
  const id = Number(value);
  return Number.isSafeInteger(id) ? String(id) : '';
};

export const normalizePublicFormCreationSettings = (value) => ({
  assigned_user_id: normalizeCreationId(value?.assigned_user_id),
  assigned_attorney_id: normalizeCreationId(value?.assigned_attorney_id),
  location_id: normalizeCreationId(value?.location_id),
});

export const validatePublicFormCreationSettings = (value, options) => {
  const errors = {};
  if (['create_case', 'create_lead'].includes(value?.target_mode ?? value?.entity_mode)) {
    const settings = normalizePublicFormCreationSettings(value?.creation_settings);
    if (!settings.assigned_user_id) errors.assigned_user_id = 'Choose an assigned user before publishing or creating a link.';
    if (!settings.location_id) errors.location_id = 'Choose a location before publishing or creating a link.';
    if (options) {
      [['assigned_user_id', 'users'], ['assigned_attorney_id', 'attorneys'], ['location_id', 'locations']]
        .forEach(([key, list]) => {
          if (settings[key] && !options[list]?.some((option) => String(option.id) === settings[key])) {
            errors[key] = 'This saved selection is unavailable. Choose an available option.';
          }
        });
    }
  }
  return { valid: Object.keys(errors).length === 0, errors };
};

export const sanitizePublicFormCreationOptions = (value) => {
  const normalizeList = (items) => {
    const seen = new Set();
    return (Array.isArray(items) ? items : []).flatMap((item) => {
      const id = normalizeCreationId(item?.id);
      const name = cleanText(item?.name, 200);
      if (!id || !name || item?.eligible === false || seen.has(id)) return [];
      seen.add(id);
      return [{ id, name }];
    });
  };
  return {
    users: normalizeList(value?.users),
    attorneys: normalizeList(value?.attorneys),
    locations: normalizeList(value?.locations),
  };
};

export const createEmptyPublicFormDraft = () => ({
  id: null,
  name: '',
  title: '',
  description: '',
  confirmation_message: 'Thank you. Your information was submitted securely.',
  target_mode: 'existing_case',
  target_id: '',
  case_type_id: '',
  creation_settings: normalizePublicFormCreationSettings(null),
  fields: [],
  expires_at: '',
  max_submissions: 1,
  notify_creator: true,
  notification_user_ids: [],
  notify_creator_in_app: true,
  notify_creator_email: false,
  notification_in_app_user_ids: [],
  notification_email_user_ids: [],
  status: 'draft',
  publication_current: false,
  requires_republish: false,
  active_schema_version: null,
  latest_schema_version: 0,
  link_count: 0,
  version: 1,
});

export const normalizePublicFormDraft = (value) => {
  const empty = createEmptyPublicFormDraft();
  const draft = value && typeof value === 'object' ? value : {};
  const requestedMode = draft.target_mode ?? draft.entity_mode;
  const targetMode = ALLOWED_TARGET_MODES.has(requestedMode) ? requestedMode : empty.target_mode;
  const rawFields = Array.isArray(draft.items)
    ? draft.items
    : Array.isArray(draft.fields)
    ? draft.fields
    : Array.isArray(draft.schema)
      ? draft.schema
      : Array.isArray(draft.schema?.items)
        ? draft.schema.items
        : Array.isArray(draft.schema?.fields)
          ? draft.schema.fields
          : [];
  const fields = Array.isArray(rawFields)
    ? rawFields.slice(0, 100).map((field, index) => {
      const rawContentType = String(field?.content_type ?? field?.type ?? '').trim().toLowerCase();
      const contentItem = field?.kind === 'content'
        || CONTENT_TYPES.has(String(field?.content_type || '').trim().toLowerCase())
        || (field?.source === 'presentation' && CONTENT_TYPES.has(rawContentType));
      if (contentItem) {
        if (!CONTENT_TYPES.has(rawContentType)) return null;
        const shortContent = rawContentType === 'subtitle' || rawContentType === 'step_title';
        const text = cleanText(field?.text ?? field?.label ?? field?.content, shortContent ? 200 : 4000);
        return {
          instance_id: cleanText(field.instance_id, 180) || `content.${rawContentType}:saved:${index}`,
          kind: 'content',
          content_type: rawContentType,
          source: 'presentation',
          type: rawContentType,
          text,
          step: Math.max(1, Math.min(20, Number.parseInt(field.step, 10) || 1)),
          repeat_group: normalizeRepeatGroup(field.repeat_group, rawContentType),
        };
      }
      const sourceId = cleanFieldId(field?.field_key ?? field?.source_field_id ?? field?.uid ?? field?.id);
      if (!sourceId) return null;
      const fieldType = normalizeFieldType(field.type ?? field.control_type ?? field.input_type);
      const source = sourceId.startsWith('question.')
        ? 'custom_question'
        : fieldType === 'upload'
          ? 'document_upload'
          : cleanText(field.source, 80);
      const normalizedFieldOptions = Array.isArray(field.options) && field.options.length > 0
        ? normalizeOptions(field.options)
        : parsePublicFormOptionLines(field.options_text);
      return {
        instance_id: cleanText(field.instance_id, 180) || `${sourceId}:saved:${index}`,
        field_key: sourceId,
        field_id: Number(field?.field_id) > 0 ? Number(field.field_id) : null,
        label: cleanText(field.label ?? field.name, 160) || 'Untitled field',
        type: fieldType,
        source,
        help_text: cleanText(field.help_text, 500),
        placeholder: cleanText(field.placeholder, 200),
        options: normalizedFieldOptions,
        options_text: typeof field.options_text === 'string'
          ? field.options_text
          : normalizedFieldOptions.map((option) => option.label).join('\n'),
        required: field.required === true,
        repeatable: field.repeatable === true && !NON_REPEATABLE_TYPES.has(fieldType),
        step: Math.max(1, Math.min(20, Number.parseInt(field.step, 10) || 1)),
        condition: normalizeCondition(field.condition),
      };
    }).filter(Boolean)
    : [];
  const notificationRecipients = Array.isArray(draft.notification_recipients)
    ? draft.notification_recipients
    : [];
  const inAppRecipientIds = notificationRecipients
    .filter((recipient) => recipient?.in_app === true)
    .map((recipient) => recipient.user_id);
  const emailRecipientIds = notificationRecipients
    .filter((recipient) => recipient?.email === true)
    .map((recipient) => recipient.user_id);
  const creatorPreference = draft.notify_creator && typeof draft.notify_creator === 'object'
    ? draft.notify_creator
    : null;
  const status = ['draft', 'published', 'revoked', 'expired'].includes(draft.status) ? draft.status : 'draft';
  const publicationCurrent = status === 'published' && draft.publication_current === true;

  return {
    ...empty,
    id: Number(draft.id) > 0 ? Number(draft.id) : null,
    name: cleanText(draft.name, 160),
    title: cleanText(draft.title, 200),
    description: cleanText(draft.description, 1500),
    confirmation_message: cleanText(draft.confirmation_message, 500) || empty.confirmation_message,
    target_mode: targetMode,
    target_id: ['existing_case', 'existing_lead'].includes(targetMode) && Number(draft.target_id) > 0
      ? String(Number(draft.target_id))
      : '',
    case_type_id: Number(draft.case_type_id) > 0 ? String(Number(draft.case_type_id)) : '',
    creation_settings: normalizePublicFormCreationSettings(draft.creation_settings),
    fields,
    expires_at: cleanText(draft.expires_at, 40),
    max_submissions: Math.max(1, Math.min(10000, Number.parseInt(draft.max_submissions, 10) || 1)),
    notify_creator: draft.notify_creator !== false,
    notification_user_ids: Array.isArray(draft.notification_user_ids)
      ? [...new Set(draft.notification_user_ids.map(Number).filter((id) => Number.isInteger(id) && id > 0))]
      : [],
    notify_creator_in_app: creatorPreference
      ? creatorPreference.in_app !== false
      : draft.notify_creator_in_app !== false,
    notify_creator_email: creatorPreference
      ? creatorPreference.email === true
      : draft.notify_creator_email === true,
    notification_in_app_user_ids: normalizeUserIds(
      draft.notification_in_app_user_ids ?? (inAppRecipientIds.length > 0 ? inAppRecipientIds : draft.notification_user_ids),
    ),
    notification_email_user_ids: normalizeUserIds(
      draft.notification_email_user_ids ?? emailRecipientIds,
    ),
    status,
    publication_current: publicationCurrent,
    requires_republish: status === 'published' && (draft.requires_republish === true || !publicationCurrent),
    active_schema_version: Number(draft.active_schema_version) > 0 ? Number(draft.active_schema_version) : null,
    latest_schema_version: Math.max(0, Number.parseInt(draft.latest_schema_version, 10) || 0),
    link_count: Math.max(0, Number.parseInt(draft.link_count, 10) || 0),
    version: Math.max(1, Number.parseInt(draft.version, 10) || 1),
  };
};

function normalizeCondition(condition) {
  if (!condition || typeof condition !== 'object') return null;
  const fieldId = cleanFieldId(
    condition.field_key ?? condition.field_uid ?? condition.uid ?? condition.field_instance_id ?? condition.field_id,
  );
  const operator = ['equals', 'not_equals', 'contains', 'not_empty', 'empty'].includes(condition.operator)
    ? condition.operator
    : 'equals';
  if (!fieldId) return null;
  return {
    field_key: fieldId,
    operator,
    value: cleanText(condition.value, 240),
  };
}

function normalizeUserIds(value) {
  return Array.isArray(value)
    ? [...new Set(value.map(Number).filter((id) => Number.isInteger(id) && id > 0))]
    : [];
}

export const publicFormTargetSupportsUploads = (targetMode) => (
  ['existing_case', 'existing_lead', 'create_lead'].includes(targetMode)
);

export const validatePublicFormDraft = (value) => {
  const draft = normalizePublicFormDraft(value);
  const errors = {};
  if (!draft.name) errors.name = 'Give this form an internal name.';
  if (!draft.title) errors.title = 'Add a title that recipients will recognize.';
  if (!draft.case_type_id) errors.case_type_id = 'Choose the case type this form belongs to.';
  const answerFields = draft.fields.filter((field) => !isPublicFormContentItem(field));
  if (answerFields.length === 0) errors.fields = 'Add at least one question.';
  if (!publicFormTargetSupportsUploads(draft.target_mode) && answerFields.some((field) => field.type === 'upload')) {
    errors.fields = 'Document uploads are not available for forms that create a new case yet. Choose another destination or remove the upload question.';
  }
  if (draft.fields.some((field) => isPublicFormContentItem(field) && !field.text)) {
    errors.fields = 'Every heading and instruction needs text.';
  }
  const stepMetadata = new Set();
  draft.fields.forEach((item) => {
    if (!isPublicFormStepContentItem(item)) return;
    const key = `${item.step}:${item.content_type}`;
    if (stepMetadata.has(key)) errors.fields = 'Each form step can have only one title and one instruction.';
    stepMetadata.add(key);
  });
  draft.fields.forEach((item, index) => {
    if (!isPublicFormContentItem(item) || !item.repeat_group?.enabled) return;
    const members = [];
    for (let memberIndex = index + 1; memberIndex < draft.fields.length; memberIndex += 1) {
      const candidate = draft.fields[memberIndex];
      if (candidate.step !== item.step || (isPublicFormContentItem(candidate) && candidate.content_type === 'subtitle')) break;
      if (!isPublicFormContentItem(candidate)) members.push(candidate);
    }
    if (members.length === 0) errors.fields = 'Every repeatable section needs at least one question after its subtitle.';
    if (members.some((field) => field.type === 'upload')) {
      errors.fields = 'Document upload questions cannot be placed inside a repeatable section.';
    }
  });
  if (answerFields.some((field) => !field.label)) errors.fields = 'Every field needs a label.';
  if (draft.fields.some((field) => (
    field.source === 'custom_question'
    && ['select', 'checkboxes'].includes(field.type)
    && field.options.length < 2
  ))) {
    errors.fields = 'Every custom choice question needs at least two unique options.';
  }
  if (draft.expires_at) {
    const expiration = new Date(draft.expires_at);
    if (Number.isNaN(expiration.getTime()) || expiration.getTime() <= Date.now()) {
      errors.expires_at = 'Choose an expiration date in the future.';
    }
  }
  return { valid: Object.keys(errors).length === 0, errors, draft };
};

export const buildPublicFormMutation = (value) => {
  const { draft } = validatePublicFormDraft(value);
  return {
    id: draft.id,
    version: draft.version,
    name: draft.name,
    title: draft.title,
    description: draft.description,
    confirmation_message: draft.confirmation_message,
    entity_mode: draft.target_mode,
    case_type_id: draft.case_type_id ? Number(draft.case_type_id) : null,
    ...(['create_case', 'create_lead'].includes(draft.target_mode) ? {
      creation_settings: Object.fromEntries(Object.entries(draft.creation_settings)
        .map(([key, id]) => [key, id ? Number(id) : null])),
    } : {}),
    schema: draft.fields.map((field, order) => (isPublicFormContentItem(field) ? {
      instance_id: field.instance_id,
      kind: 'content',
      content_type: field.content_type,
      source: 'presentation',
      type: field.content_type,
      text: field.text,
      step: field.step,
      order,
      ...(field.repeat_group?.enabled ? { repeat_group: field.repeat_group } : {}),
    } : {
      instance_id: field.instance_id,
      field_key: field.field_key,
      field_id: field.field_id,
      source: field.source,
      label: field.label,
      type: field.type,
      help_text: field.help_text,
      placeholder: field.placeholder,
      options: field.options,
      required: field.required,
      repeatable: field.repeatable,
      step: field.step,
      condition: field.condition,
      order,
    })),
    notify_creator: {
      in_app: draft.notify_creator_in_app,
      email: draft.notify_creator_email,
    },
    notification_recipients: [...new Set([
      ...draft.notification_in_app_user_ids,
      ...draft.notification_email_user_ids,
    ])].map((userId) => ({
      user_id: userId,
      in_app: draft.notification_in_app_user_ids.includes(userId),
      email: draft.notification_email_user_ids.includes(userId),
    })),
  };
};

export const publicFormTargetLabel = (mode) => ({
  existing_case: 'Existing case',
  existing_lead: 'Existing lead',
  create_case: 'Create a new case',
  create_lead: 'Create a new lead',
}[mode] || 'Unknown target');

export const normalizePublicFormPageUrl = (configuredBaseUrl) => {
  const fallbackBase = typeof window !== 'undefined' ? `${window.location.origin}/forms/index.html` : '';
  const base = String(configuredBaseUrl || process.env.REACT_APP_PUBLIC_FORMS_URL || fallbackBase).trim();
  if (!base) return '';
  try {
    const url = new URL(base, typeof window !== 'undefined' ? window.location.href : undefined);
    const hostname = url.hostname.toLowerCase().replace(/^\[|\]$/g, '').replace(/\.$/, '');
    const loopback = hostname === 'localhost'
      || hostname === '::1'
      || hostname === '127.0.0.1';
    if ((url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback))
      || url.username
      || url.password
      || url.search
      || url.hash) return '';
    const trimmedPath = url.pathname.replace(/\/+$/, '') || '/forms';
    const lastSegment = trimmedPath.split('/').pop() || '';
    url.pathname = lastSegment.includes('.') ? trimmedPath : `${trimmedPath}/index.html`;
    url.hash = '';
    return url.toString();
  } catch (error) {
    return '';
  }
};

export const buildPublicFormLink = (fragment, configuredBaseUrl) => {
  const rawFragment = String(fragment || '').replace(/^#/, '').trim();
  if (!rawFragment || /[\s?#]/.test(rawFragment)) return '';
  const pageUrl = normalizePublicFormPageUrl(configuredBaseUrl);
  if (!pageUrl) return '';
  const url = new URL(pageUrl);
  url.hash = rawFragment;
  return url.toString();
};
